import { ReturnRabbit } from '../../../../constants/upsell-modal-content-codes';
import { BillingPlanIds, isStarterPlan, isStandardPlan, isPremiumPlan, isXMigrationPlanCode } from '../../../../enums/billing-plan-ids';
import { getReturnRabbitAddOn } from '../../api/add-ons-api';

/**
 * Pure utility functions for plan operations that don't depend on React hooks or context.
 * Separated to avoid import cycles between use-plan-details and manage-subscription-drawer-context.
 */

const ANNUAL_SUFFIX = /_annual$/i;
const stripAnnualSuffix = (code) => code?.replace(ANNUAL_SUFFIX, '') ?? '';

/**
 * Finds the annual equivalent plan ID by matching on normalized `code` (strip `_annual` suffix).
 * @param {string} planId
 * @param {import('../../../../prop-types/subscription-data-types.d.ts').BillingPlan[]} plans
 * @returns {string | null}
 */
export const findAnnualPlanId = (planId, plans) => {
  const sourcePlan = plans.find((p) => p.billingPlanId === planId);
  if (!sourcePlan?.code) return null;
  const baseCode = stripAnnualSuffix(sourcePlan.code);
  const annualPlan = plans.find((p) => p.isOnAnnualBilling && stripAnnualSuffix(p.code) === baseCode);
  return annualPlan?.billingPlanId ?? null;
};

const XMIGRATION_TIER_TO_ANNUAL_ID = Object.freeze({
  starter: BillingPlanIds.Starter2025Q2Annual.toString(),
  standard: BillingPlanIds.Standard2025Q2Annual.toString(),
  premium: BillingPlanIds.Premium2025Q2Annual.toString(),
});

/**
 * Maps an XMigration plan to its GBB annual equivalent by tier.
 * @param {string} planId - An XMigration monthly plan billingPlanId
 * @param {import('../../../../prop-types/subscription-data-types.d.ts').BillingPlan[]} plans
 * @returns {string | null}
 */
export const findXMigrationAnnualPlanId = (planId, plans) => {
  let targetAnnualPlanId = null;

  if (isStarterPlan(planId)) {
    targetAnnualPlanId = XMIGRATION_TIER_TO_ANNUAL_ID.starter;
  } else if (isStandardPlan(planId)) {
    targetAnnualPlanId = XMIGRATION_TIER_TO_ANNUAL_ID.standard;
  } else if (isPremiumPlan(planId)) {
    targetAnnualPlanId = XMIGRATION_TIER_TO_ANNUAL_ID.premium;
  }

  if (!targetAnnualPlanId) return null;

  const annualPlanExists = plans.some((p) => p.billingPlanId === targetAnnualPlanId);
  return annualPlanExists ? targetAnnualPlanId : null;
};

/**
 * Finds the monthly equivalent plan ID by matching on normalized `code` (strip `_annual` suffix).
 * @param {string} planId
 * @param {import('../../../../prop-types/subscription-data-types.d.ts').BillingPlan[]} plans
 * @returns {string | null}
 */
export const findMonthlyPlanId = (planId, plans) => {
  const sourcePlan = plans.find((p) => p.billingPlanId === planId);
  if (!sourcePlan?.code) return null;
  const baseCode = stripAnnualSuffix(sourcePlan.code);
  const monthlyPlan = plans.find((p) => !p.isOnAnnualBilling && stripAnnualSuffix(p.code) === baseCode);
  return monthlyPlan?.billingPlanId ?? null;
};

/**
 * Gets the appropriate initial shipment limit for a plan based on the current subscription state.
 * For the current billing plan, returns the current shipment limit.
 * For other plans with tiers, finds the matching tier or appropriate fallback.
 * @param {object} plan - The plan to get the shipment limit for
 * @param {string} currentBillingPlanId - The current billing plan ID
 * @param {number} currentSubscriptionShipmentLimit - The current subscription's shipment limit
 * @returns {number} The initial shipment limit to use for this plan
 */
export const getInitialShipmentLimitForPlan = (plan, currentBillingPlanId, currentSubscriptionShipmentLimit) => {
  if (plan?.billingPlanId === currentBillingPlanId) {
    return currentSubscriptionShipmentLimit;
  }

  const tiers = plan?.shipmentLimit?.tiers;
  if (!tiers?.length) {
    return plan?.shipmentLimit?.limit || 0;
  }

  const matchingTier = tiers.find(({ endingQuantity }) => endingQuantity === currentSubscriptionShipmentLimit);
  if (matchingTier) {
    return matchingTier.endingQuantity;
  }

  const highestAvailableShipmentLimit = tiers[tiers.length - 1].endingQuantity;
  if (currentSubscriptionShipmentLimit > highestAvailableShipmentLimit) {
    return highestAvailableShipmentLimit;
  }

  return tiers[0].endingQuantity;
};

// Gotchas for finding a plan reference by Id.
// 1. The planId is a BYOA enabled planId. These are not part of allPlans.
// 2. The planId is based on an plan that is not part of their planProfile, and therefore allPlans (exceptional case).
// 3. The planId is from defaultProfilePlans (migration target profile), not the seller's current allPlans.
export const findPlanReference = (targetPlanId, subscriptionData) => {
  const { allPlans = [], currentBillingPlan = {}, planToNonWalletAccessPlanMap = {}, defaultProfilePlans } = subscriptionData ?? {};

  const targetPlan = allPlans.find(({ billingPlanId }) => billingPlanId === targetPlanId);
  const targetDefaultProfilePlan = defaultProfilePlans?.find(({ billingPlanId }) => billingPlanId === targetPlanId);
  const targetNonWalletAccessPlan = Object.values(planToNonWalletAccessPlanMap).find(({ billingPlanId }) => billingPlanId === targetPlanId);

  const currentNonWalletAccessPlan = Object.values(planToNonWalletAccessPlanMap).find(
    ({ billingPlanId }) => billingPlanId === currentBillingPlan?.billingPlanId,
  );
  const fallbackPlan = currentNonWalletAccessPlan ?? currentBillingPlan;

  return targetPlan ?? targetDefaultProfilePlan ?? targetNonWalletAccessPlan ?? fallbackPlan;
};

// Gotchas for finding a plan reference by code:
// 1. The code is a BYOA enabled planId. These are not part of allPlans.
// 2. The code is based on an plan that is not part of their planProfile, and therefore allPlans (exceptional case).
// 3. The code is from defaultProfilePlans (migration target profile), not the seller's current allPlans.
// 4. The code is an XMigration code, where AllPlans will always use the generic {tier}_X_migration pattern
export const findPlanReferenceByCode = (inputPlanCode, subscriptionData) => {
  const { allPlans = [], currentBillingPlan = {}, planToNonWalletAccessPlanMap = {}, defaultProfilePlans } = subscriptionData ?? {};
  let planCode = inputPlanCode;
  if (isXMigrationPlanCode(planCode)) {
    const split = planCode.split('_');
    const genericCode = `${split[0]}_X_${split[2]}`;
    planCode = genericCode;
  }

  const targetPlan = allPlans.find(({ code }) => code === planCode || code === inputPlanCode);
  const targetDefaultProfilePlan = defaultProfilePlans?.find(({ code }) => code === planCode);
  const targetNonWalletAccessPlan = Object.values(planToNonWalletAccessPlanMap).find(({ code }) => code === planCode);

  const currentNonWalletAccessPlan = Object.values(planToNonWalletAccessPlanMap).find(
    ({ billingPlanId }) => billingPlanId === currentBillingPlan?.billingPlanId,
  );
  const fallbackPlan = currentNonWalletAccessPlan ?? currentBillingPlan;

  return targetPlan ?? targetDefaultProfilePlan ?? targetNonWalletAccessPlan ?? fallbackPlan;
};

export const isFeatureIncludedInBillingPlan = (plan, featureId) => {
  const lineItems = plan?.subscriptionLineItems ?? [];
  return lineItems.some((lineItem) => lineItem.featureIds?.[0] === featureId && lineItem.isIncludedInPlan);
};

const getIncludedFeatures = (plan) => {
  const lineItems = plan?.subscriptionLineItems ?? [];
  return lineItems.reduce((acc, lineItem) => {
    const featureId = lineItem.featureIds?.[0];
    if (lineItem.isIncludedInPlan && !acc.some((feature) => feature?.featureIds[0] === featureId)) {
      acc.push(lineItem);
    }
    return acc;
  }, []);
};

// NOTE: if the currentBillingPlan is null, we are in a trial or expired plan state
export const getCurrentLineItems = (subscriptionData, planInfo) => {
  const hasActiveSubscription = !!subscriptionData?.currentBillingPlan;
  const { currentBillingPlan } = subscriptionData ?? {};
  const { plan } = planInfo ?? {};
  const resubscriptionLineItems = plan?.subscriptionLineItems ?? [];
  const currentSubscriptionLineItems = currentBillingPlan?.subscriptionLineItems ?? [];

  const returnRabbitAddOn = getReturnRabbitAddOn();

  if (returnRabbitAddOn?.isActive) {
    const returnRabbitLineItem = {
      addOnCode: ReturnRabbit,
      deactivationDateTimeUtc: null,
      featureIds: [ReturnRabbit],
      isAlphaUser: false,
      isIncludedInPlan: false,
      isTrial: false,
      trialEndDateTimeUtc: null,
    };

    currentSubscriptionLineItems.push(returnRabbitLineItem);
    // Note: intentionally not in resubscriptionLineItems as the user cannot resubscribe to ReturnRabbit via changePlan
  }

  return hasActiveSubscription ? currentSubscriptionLineItems : resubscriptionLineItems;
};

// AddOnsToAdd: [CurrentPlan AddOns] (that are included in plan) DIFF [SelectedPlan Features]
// AddOnsToRemove: [CurrentPlan AddOns] (that are not included in plan) INTERSECT [SelectedPlan Features]
export const getAddOnsToAddAndRemove = (selectedBillingPlanId, subscriptionData, marketplaceAddOns, planInfo) => {
  const hasActiveSubscription = !!subscriptionData?.currentBillingPlan; // NOTE: currentBillingPlan is null when the trial or plan is expired

  const { currentBillingPlan } = subscriptionData;
  const currentPlan = findPlanReference(currentBillingPlan?.billingPlanId, subscriptionData);
  const selectedPlan = findPlanReference(selectedBillingPlanId, subscriptionData);
  const currentLineItems = getCurrentLineItems(subscriptionData, planInfo);

  const currentPlanEnabledAddOns = hasActiveSubscription ? currentLineItems.filter((a) => a.addOnCode && !a.isIncludedInPlan) : [];

  const currentPlanIncludedFeatures = getIncludedFeatures(currentPlan);
  const selectedPlanIncludedFeatures = getIncludedFeatures(selectedPlan);

  const addOnsToAdd = currentLineItems.reduce((acc, lineItem) => {
    const featureId = lineItem.featureIds?.[0];
    const { isAlphaUser } = lineItem;
    const isInTrial = lineItem.isTrial;
    const lineItemIsIncludedInSelectedPlan = selectedPlanIncludedFeatures.some((f) => f.featureIds[0] === featureId);
    const matchingAddOnAvailableForPurchase = marketplaceAddOns.find((marketplaceAddOn) => marketplaceAddOn.code === featureId);
    const addOnAlreadyEnabled = currentPlanEnabledAddOns.some(
      (a) => !!matchingAddOnAvailableForPurchase && a.addOnCode === matchingAddOnAvailableForPurchase.code,
    );

    // If you have this feature as an alpha user or you are actively trialing this feature, don't queue the addOn for purchase
    if (isAlphaUser || isInTrial) {
      return acc;
    }

    // If the feature is included in the selected plan, don't queue the addOn for purchase
    if (lineItemIsIncludedInSelectedPlan) {
      return acc;
    }

    if (lineItem.deactivationDateTimeUtc && !lineItemIsIncludedInSelectedPlan) {
      return acc;
    }

    // If the addOn is not available on the marketplace or already enabled/purchase, don't queue the addOn for purchase
    if (!matchingAddOnAvailableForPurchase || addOnAlreadyEnabled) {
      return acc;
    }

    // Otherwise, we queue it for purchase
    acc.push(matchingAddOnAvailableForPurchase.code);
    return acc;
  }, []);

  const addOnsToRemove = currentLineItems.reduce((acc, lineItem) => {
    // NOTE: currently there is a 1:1 mapping between featureId and addOn code, this will change when bundles are introduced
    // TODO: link ticket
    const featureId = lineItem.featureIds?.[0];
    const lineItemIsIncludedInSelectedPlan = selectedPlanIncludedFeatures.some((f) => f.featureIds[0] === featureId);
    const matchingAddOnAvailableForPurchase = marketplaceAddOns.find((marketplaceAddOn) => marketplaceAddOn.code === featureId);

    if (lineItem.isAlphaUser) {
      return acc;
    }

    if (!lineItemIsIncludedInSelectedPlan) {
      return acc;
    }

    if (!matchingAddOnAvailableForPurchase) {
      return acc;
    }

    if (lineItem.deactivationDateTimeUtc && !lineItemIsIncludedInSelectedPlan) {
      return acc;
    }

    if (!acc.includes(matchingAddOnAvailableForPurchase.code)) {
      acc.push(matchingAddOnAvailableForPurchase.code);
    }
    return acc;
  }, []);

  return {
    addOnsToAdd,
    addOnsToRemove,
    currentPlanIncludedFeatures,
    selectedPlanIncludedFeatures,
  };
};
